export const DRIVE_FOLDER_MIME_TYPE = 'application/vnd.google-apps.folder';

export type DriveBrowserItem = {
  id: string;
  name: string;
  mimeType: string;
  path: string;
  createdTime?: string;
  modifiedTime?: string;
  size?: string;
  webViewLink?: string;
};

export type DriveBrowserIndex = {
  byPath: Map<string, DriveBrowserItem>;
  childrenByParent: Map<string, DriveBrowserItem[]>;
};

export type DriveFileIconKind =
  | 'folder'
  | 'google-sheets'
  | 'excel'
  | 'google-docs'
  | 'word'
  | 'google-slides'
  | 'powerpoint'
  | 'pdf'
  | 'image'
  | 'archive'
  | 'audio'
  | 'video'
  | 'text'
  | 'file';

const italianCollator = new Intl.Collator('it', { numeric: true, sensitivity: 'base' });

export function isDriveFolder(item: Pick<DriveBrowserItem, 'mimeType'>): boolean {
  return item.mimeType === DRIVE_FOLDER_MIME_TYPE;
}

export function driveFileIconKind(
  item: Pick<DriveBrowserItem, 'mimeType' | 'name'>,
): DriveFileIconKind {
  if (isDriveFolder(item)) return 'folder';

  const mimeType = item.mimeType.toLowerCase();
  const extension = item.name.split('.').pop()?.toLowerCase() ?? '';

  if (mimeType === 'application/vnd.google-apps.spreadsheet') return 'google-sheets';
  if (mimeType === 'application/vnd.google-apps.document') return 'google-docs';
  if (mimeType === 'application/vnd.google-apps.presentation') return 'google-slides';
  if (
    mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    || mimeType === 'application/vnd.ms-excel'
    || ['xls', 'xlsx', 'xlsm', 'ods', 'csv'].includes(extension)
  ) return 'excel';
  if (
    mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    || mimeType === 'application/msword'
    || ['doc', 'docx', 'odt', 'rtf'].includes(extension)
  ) return 'word';
  if (
    mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    || mimeType === 'application/vnd.ms-powerpoint'
    || ['ppt', 'pptx', 'odp'].includes(extension)
  ) return 'powerpoint';
  if (mimeType === 'application/pdf' || extension === 'pdf') return 'pdf';
  if (mimeType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) return 'image';
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) return 'archive';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('text/') || ['txt', 'md'].includes(extension)) return 'text';
  return 'file';
}

export function driveParentPath(path: string): string {
  const separator = path.lastIndexOf('/');
  return separator === -1 ? '' : path.slice(0, separator);
}

export function buildDriveBrowserIndex(items: DriveBrowserItem[]): DriveBrowserIndex {
  const byPath = new Map<string, DriveBrowserItem>();
  const childrenByParent = new Map<string, DriveBrowserItem[]>();

  for (const item of items) {
    if (!item.path) continue;
    byPath.set(item.path, item);
    const parentPath = driveParentPath(item.path);
    const children = childrenByParent.get(parentPath) ?? [];
    children.push(item);
    childrenByParent.set(parentPath, children);
  }

  for (const children of childrenByParent.values()) {
    children.sort((left, right) => {
      const folderOrder = Number(isDriveFolder(right)) - Number(isDriveFolder(left));
      return folderOrder || italianCollator.compare(left.name, right.name);
    });
  }

  return { byPath, childrenByParent };
}

export function driveFileKind(item: Pick<DriveBrowserItem, 'mimeType' | 'name'>): string {
  if (isDriveFolder(item)) return 'Cartella';

  const types: Record<string, string> = {
    'application/vnd.google-apps.spreadsheet': 'Foglio Google',
    'application/vnd.google-apps.presentation': 'Presentazione Google',
    'application/vnd.google-apps.document': 'Documento Google',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'File Excel',
    'application/vnd.ms-excel': 'File Excel',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'Presentazione PowerPoint',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Documento Word',
    'application/pdf': 'Documento PDF',
  };

  return types[item.mimeType] ?? item.name.split('.').pop()?.toUpperCase() ?? 'File';
}

export function formatDriveFileSize(value?: string): string | null {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return null;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
